/**
 * LimeChat WhatsApp Widget Loader
 * Handles loading and initialization of the LimeChat widget with proper error handling
 */

(function() {
  'use strict';

  /**
   * Initialize LimeChat widget with given configuration
   * @param {Object} config - Widget configuration object
   * @param {string} config.accessToken - Access token for authentication
   * @param {string} config.phoneNumber - Phone number for WhatsApp
   */
  function initLimeChatWidget(config) {
    if (!config || !config.accessToken || !config.phoneNumber) {
      console.error('[LimeChat] Invalid configuration provided');
      return;
    }

    try {
      // Verify LimeChatWhatsapp is defined before using it
      if (typeof LimeChatWhatsapp !== 'undefined') {
        const widget = new LimeChatWhatsapp(config);
        widget.initializeWidget();
        console.log('[LimeChat] Widget initialized successfully');
        window.limeChatLoaded = true;
      } else {
        console.warn('[LimeChat] LimeChatWhatsapp is not defined after script load');
      }
    } catch (error) {
      console.error('[LimeChat] Error initializing widget:', error);
      // Report to Sentry if available
      if (window.Sentry && window.Sentry.captureException) {
        window.Sentry.captureException(error, {
          tags: { component: 'limechat-widget' },
          extra: { config: { phoneNumber: config.phoneNumber } }
        });
      }
    } finally {
      window.limeChatLoading = false;
    }
  }

  /**
   * Load LimeChat widget script
   * @param {Object} config - Widget configuration
   * @param {number} retryCount - Number of retries attempted (default: 0)
   */
  function loadLimeChatScript(config, retryCount = 0) {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 2000;
    const SCRIPT_URL = 'https://s3.ap-south-1.amazonaws.com/cdn.limechat.ai/packs/js/whatsapp_widget/lc_wa_widget.js';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.defer = true;
    script.src = SCRIPT_URL;

    script.onload = function() {
      console.log('[LimeChat] Script loaded successfully');
      initLimeChatWidget(config);
    };

    script.onerror = function() {
      console.error('[LimeChat] Failed to load widget script');
      window.limeChatLoading = false;

      // Retry logic for unstable networks (UC Browser, etc.)
      if (retryCount < MAX_RETRIES) {
        console.log(`[LimeChat] Retrying... (${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => {
          loadLimeChatScript(config, retryCount + 1);
        }, RETRY_DELAY);
      } else {
        console.error('[LimeChat] Max retries reached, giving up');
        // Report to Sentry if available
        if (window.Sentry && window.Sentry.captureMessage) {
          window.Sentry.captureMessage('LimeChat script failed to load after retries', {
            level: 'warning',
            tags: { component: 'limechat-widget' },
            extra: { retryCount, url: SCRIPT_URL }
          });
        }
      }
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
  }

  // Store pending config for delayed initialization
  let pendingConfig = null;

  // Expose global function to initialize LimeChat
  window.initializeLimeChat = function(config) {
    // Already loaded and initialized
    if (window.limeChatLoaded) {
      console.log('[LimeChat] Already initialized');
      return;
    }

    // Already loading - store the first valid config
    if (window.limeChatLoading) {
      console.log('[LimeChat] Script already loading, skipping duplicate request');
      // Store config if we don't have one yet (first caller wins)
      if (!pendingConfig && config && config.accessToken && config.phoneNumber) {
        pendingConfig = config;
      }
      return;
    }

    // First call - start loading
    window.limeChatLoading = true;
    pendingConfig = config;
    loadLimeChatScript(config);
  };

})();